import { faExchange } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Card,
  Divider,
  Flex,
  Heading,
  Popover,
  PopoverContent,
  PopperTrigger,
  SxProps,
  Text,
  Toast,
} from '@modusign/design-system';
import { useGetUserInfo } from '@modusign/openapi-client-user';
import md5 from 'crypto-js/md5';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { useToggle } from 'react-use';

import { useOtherWorkspace } from '@app/hooks/workspace';
import pushAdobeDataLayer from '@app/libs/analytics/adobe';
import josa from '@app/libs/josa';
import workspaceService from '@app/libs/workspaceService';
import { signOut } from '@app/store/account/sign-out/actions';

const PROFILE_COLOR_PALETTE = [
  '#FFEF99',
  '#FFE9B2',
  '#FFE0B2',
  '#FFD5D5',
  '#D0E1FF',
  '#DCE1FF',
  '#ADE8DA',
  '#B8FAEB',
  '#EAF9B0',
  '#F8D7FE',
  '#D9DBE0',
];

function getUserAvatarProps({ name, email }: { name: string; email: string }) {
  const avatarContent = name.trim().toUpperCase()[0];
  const arrayIdxFromHash = Math.abs(
    md5(email.trim().toLowerCase()).words.reduce((acc, v) => acc + v, 0) %
      PROFILE_COLOR_PALETTE.length
  );

  return { children: avatarContent, sx: { bg: PROFILE_COLOR_PALETTE[arrayIdxFromHash] } };
}

function createMultiLineEllipsisStyle(line: number) {
  return {
    overflow: 'hidden',
    whiteSpace: 'normal',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-line-clamp': `${line}`,
    '-webkit-box-orient': 'vertical',
    wordBreak: 'break-word',
  };
}

export default function MyProfile() {
  const dispatch = useDispatch();
  const history = useHistory();
  const [isOpen, toggle] = useToggle(false);

  const me = useGetUserInfo();
  const otherWorkspace = useOtherWorkspace();
  const hasSingleWorkspace = otherWorkspace === null;

  if (!me.data) {
    return null;
  }

  const avatarProps = getUserAvatarProps(me.data.profile);

  return (
    <Popover isOpen={isOpen} mode="click" placement="bottom-end" onOpenChange={toggle}>
      <PopperTrigger>
        <Flex
          alignItems="center"
          as="button"
          justifyContent="center"
          sx={{
            width: [28, null, 32],
            height: [28, null, 32],
            borderRadius: 'circle',
            '&:hover': {
              bg: 'buttonBackgroundNeutralSlightlyDark',
            },
          }}
        >
          <Avatar diameter={28} {...avatarProps} />
        </Flex>
      </PopperTrigger>
      <PopoverContent>
        <Card
          display="flex"
          flexDirection="column"
          p={0}
          py={5}
          sx={{ gap: 4 }}
          width="260px"
          zIndex={501}
        >
          <Flex
            alignItems="center"
            px={5}
            sx={{
              '&:hover': {
                cursor: 'pointer',
              },
            }}
            onClick={() => history.push('/settings/profile')}
          >
            <Avatar diameter={40} {...avatarProps} />
            <Flex flex={1} flexDirection="column" ml={4}>
              <Heading fontSize="sm" fontWeight="medium" sx={createMultiLineEllipsisStyle(2)}>
                {me.data.profile.name}
              </Heading>
              <Text color="textNeutralLight" fontSize="sm" sx={createMultiLineEllipsisStyle(2)}>
                {me.data.profile.email}
              </Text>
            </Flex>
          </Flex>

          {!hasSingleWorkspace && (
            <>
              <Divider />
              <Flex
                as="button"
                color="textNeutralSlightlyLight"
                px={5}
                py={2}
                sx={{
                  '&:hover': {
                    bg: 'menuBackgroundNeutralDark',
                  },
                }}
                onClick={() => {
                  workspaceService.setWorkspaceId(otherWorkspace.id);
                  pushAdobeDataLayer({
                    event: 'moveToOtherWorkspace',
                    otherWorkspace,
                  });
                  Toast.success({
                    description:
                      otherWorkspace.type === 'PRIVATE'
                        ? '내 워크스페이스로 이동했습니다.'
                        : // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                          `${josa(otherWorkspace.name!, '으로/로')} 이동했습니다.`,
                  });
                }}
              >
                <Box
                  as={FontAwesomeIcon}
                  color="iconNeutralSlightlyDark"
                  icon={faExchange}
                  mr={2}
                />
                {`${otherWorkspace.type === 'PUBLIC' ? '공용' : '내'} 워크스페이스로 가기`}
              </Flex>
            </>
          )}

          <Divider />
          <Flex
            as="button"
            color="textNeutralSlightlyLight"
            px={5}
            py={2}
            sx={{
              '&:hover': {
                bg: 'menuBackgroundNeutralDark',
              },
            }}
            onClick={() => dispatch(signOut())}
          >
            로그아웃
          </Flex>
        </Card>
      </PopoverContent>
    </Popover>
  );
}

function Avatar({
  children,
  diameter,
  sx,
}: {
  children: React.ReactNode;
  diameter: number;
} & SxProps) {
  return (
    <Flex
      alignItems="center"
      borderRadius="circle"
      height={diameter}
      justifyContent="center"
      sx={sx}
      width={diameter}
    >
      <Heading fontSize={`${18 * diameter * 0.03}px`} fontWeight="medium" lineHeight="unset">
        {children}
      </Heading>
    </Flex>
  );
}
